import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileText } from 'lucide-react';
import api from '../services/api';
import { Tooltip } from './ui';

const ITEMS_PER_PAGE = 20;

const ClientesCotizaciones = () => {
    const navigate = useNavigate();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const observerRef = useRef();
    const searchTimeoutRef = useRef();
    const isFirstRender = useRef(true);

    const cargarClientes = useCallback(async (reset = false, searchTerm = '') => {
        if (reset) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            const currentOffset = reset ? 0 : offset;
            const response = await api.clientes.getWithStats({
                q: searchTerm || undefined,
                limit: ITEMS_PER_PAGE,
                offset: currentOffset
            });

            const newClientes = response.data || [];
            const pagination = response.pagination || {};

            if (reset) {
                setClientes(newClientes);
                setOffset(newClientes.length);
            } else {
                setClientes(prev => [...prev, ...newClientes]);
                setOffset(prev => prev + newClientes.length);
            }

            setHasMore(pagination.hasMore ?? newClientes.length === ITEMS_PER_PAGE);
        } catch (err) {
            console.error('Error al cargar clientes:', err);
            toast.error('Error al cargar clientes');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [offset]);

    // Cargar inicial
    useEffect(() => {
        cargarClientes(true, '');
    }, []);

    // Búsqueda con debounce
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            cargarClientes(true, busqueda);
        }, 400);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [busqueda]);

    // Infinite scroll observer
    const lastClienteRef = useCallback(node => {
        if (loading || loadingMore) return;
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                cargarClientes(false, busqueda);
            }
        }, { threshold: 0.1 });

        if (node) observerRef.current.observe(node);
    }, [loading, loadingMore, hasMore, busqueda]);

    if (loading) {
        return <div className="loading">Cargando clientes...</div>;
    }

    return (
        <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h2>Clientes - Cotizaciones</h2>
                <input
                    className="input"
                    type="text"
                    placeholder="Buscar por nombre o empresa..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    style={{ width: '280px' }}
                />
            </div>

            <div className="card-body">
                {clientes.length === 0 ? (
                    <div className="empty-state">
                        <p>{busqueda ? 'No se encontraron clientes' : 'No hay clientes registrados'}</p>
                        <p style={{ color: 'var(--muted)', fontSize: '14px', marginTop: '8px' }}>
                            Los clientes aparecerán aquí cuando estén asociados a cotizaciones
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="clientes-cot-grid">
                            {clientes.map((cliente, index) => {
                                const isLast = index === clientes.length - 1;
                                return (
                                    <div
                                        key={cliente.id}
                                        ref={isLast ? lastClienteRef : null}
                                        className="cliente-cot-card"
                                        onClick={() => navigate(`/cotizaciones/cliente/${cliente.id}`)}
                                    >
                                        <div className="cliente-cot-header">
                                            <div className="cliente-cot-info">
                                                <h3>{cliente.nombre}</h3>
                                                {cliente.empresa && (
                                                    <span className="cliente-cot-empresa">{cliente.empresa}</span>
                                                )}
                                            </div>
                                            <Tooltip content="Total de cotizaciones">
                                                <div className="cliente-cot-total-badge" tabIndex={0}>
                                                    <FileText size={14} />
                                                    <span className="total-number">{cliente.total_cotizaciones || 0}</span>
                                                </div>
                                            </Tooltip>
                                        </div>

                                        <div className="cliente-cot-stats">
                                            <div className="cliente-cot-stat borrador">
                                                <span className="stat-value">{cliente.borradores || 0}</span>
                                                <span className="stat-label">Borrador</span>
                                            </div>
                                            <div className="cliente-cot-stat enviada">
                                                <span className="stat-value">{cliente.enviadas || 0}</span>
                                                <span className="stat-label">Enviadas</span>
                                            </div>
                                            <div className="cliente-cot-stat aprobada">
                                                <span className="stat-value">{cliente.aprobadas || 0}</span>
                                                <span className="stat-label">Aprobadas</span>
                                            </div>
                                            <div className="cliente-cot-stat rechazada">
                                                <span className="stat-value">{cliente.rechazadas || 0}</span>
                                                <span className="stat-label">Rechazadas</span>
                                            </div>
                                        </div>

                                        {cliente.ultima_cotizacion && (
                                            <div className="cliente-cot-footer">
                                                <span className="cliente-cot-ultima">
                                                    Última: {new Date(cliente.ultima_cotizacion).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {loadingMore && (
                            <div className="loading" style={{ marginTop: '20px' }}>
                                Cargando más clientes...
                            </div>
                        )}

                        {!hasMore && clientes.length > 0 && (
                            <p style={{ textAlign: 'center', color: 'var(--muted)', marginTop: '20px', fontSize: '14px' }}>
                                No hay más clientes para mostrar
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ClientesCotizaciones;
